/**
 *
 * ChartResult
 *
 */

import React, { memo, FunctionComponent, useEffect, useState } from 'react';
import {
  Chart,
  ChartCategoryAxis,
  ChartCategoryAxisCrosshair,
  ChartCategoryAxisCrosshairTooltip,
  ChartCategoryAxisItem,
  ChartLegend,
  ChartLegendItem,
  ChartSeries,
  ChartSeriesItem,
  ChartValueAxis,
  ChartValueAxisItem,
} from '@progress/kendo-react-charts';
import { TestResults } from '../../../../domains/results';
import 'hammerjs';
import moment from 'moment';
import { LegendItemVisualArgs } from '@progress/kendo-react-charts/dist/npm/argument-types/legend-item-visual-args.interface';
import { Row, Wrap } from '../../../../assets/styledAssets/grid';
import {
  StyledLabel,
  StyledMultiSelect,
} from '../../../../components/FormField/styles';
import {
  generateValueAxis,
  getRandomColor,
  ISeries,
  seriesName,
  ValueAxis,
} from '../../../../helpers/chart.helper';
import Button from '../../../../components/Button/Button.component';
import {
  setSelectedColumns,
  setSelectedSummaryColumns,
} from '../../TestResults.slice';
import {
  LineGraphIcon,
  RowGraphIcon,
} from '../../../../components/Icons/Icons.component';
import {
  accentColorSecond,
  fontColorSecond,
} from '../../../../assets/styledAssets/vars';
import { ResultProperty } from '../../testresults';

interface IProps {
  chartData: TestResults[];
  selectedColumns: ResultProperty[];
  listProperties: ResultProperty[];
  listSecondProperties?: ResultProperty[];
  propertyName: string;
  secondPropertyName?: string;
  setSelectedColumns:
    | typeof setSelectedColumns
    | typeof setSelectedSummaryColumns;
  chartCategory?: 'summary' | 'raw';
}

const ChartResult: FunctionComponent<IProps> = ({
  chartData,
  selectedColumns,
  setSelectedColumns,
  listProperties,
  chartCategory,
  listSecondProperties,
  propertyName,
  secondPropertyName,
}) => {
  const [valueAxis, setValueAxis] = useState<ValueAxis[]>([]);
  const [series, setSeries] = useState<ISeries[]>();
  const [step, setStep] = useState<number>(10);
  const [chartType, setChartType] = useState<'column' | 'line'>('line');

  useEffect(() => {
    let xAxis: number[] = [];
    let xAxisAnalytics: number[] = [];
    let xAxisSummary: Date[] = [];
    if (chartData.length) {
      setStep(
        Math.round((chartData[0].raw?.properties[0].values.length || 0) / 50),
      );
      chartData[0].raw?.properties.forEach((prop) => {
        if (prop.mlPropertyNameId === 'tStp') {
          xAxis = prop.values as number[];
        }
      });
      chartData[0].analysis?.properties.forEach((prop) => {
        if (prop.mlPropertyNameId === 'tStp') {
          xAxisAnalytics = prop.values as number[];
        }
      });
      xAxisSummary = chartData.map((c) => moment(c.date).toDate());
    }

    if (!selectedColumns.length || !chartData.length) {
      xAxis = [];
      setSeries([]);
    }

    if (selectedColumns.length) {
      const selectedColumnsName = selectedColumns.map((pr) => pr.name);
      let tempSeries: ISeries[] = [];
      let tempValuesAxis: ValueAxis[] = [];
      chartData.forEach((test, indexProp) => {
        test.raw?.properties.forEach((prop) => {
          if (selectedColumnsName.includes(prop.name)) {
            const data = xAxis.map((x, idx) => ({
              x: new Date(2021, 0, 0, 0, 0, 0, x),
              y: prop.values[idx]
                ? parseFloat(prop.values[idx].toString())
                : null,
            }));
            tempSeries.push({
              name: seriesName(chartData.length, test.date, prop.name),
              shortName: prop.mlPropertyNameId,
              data: data,
              type: 'line',
              color: getRandomColor(
                chartData.length > 1
                  ? indexProp
                  : selectedColumnsName.indexOf(prop.name),
              ),
              axisName: prop.unit || prop.name,
            });

            tempValuesAxis = generateValueAxis(tempValuesAxis, prop, data);
          }
        });

        const sideValues = ((
          test.analysis?.properties.find(
            (prop) => prop.mlPropertyNameId === 'Side',
          ) || {}
        ).values || ['']) as string[];
        let sideTypes = sideValues.filter((x, i, a) => a.indexOf(x) === i);
        test.analysis?.properties
          .filter((prop) => prop.name !== 'Side')
          .forEach((prop) => {
            sideTypes = sideTypes?.length ? sideTypes : [''];
            if (selectedColumnsName.includes(prop.name)) {
              sideTypes.forEach((sideType) => {
                const data = xAxisAnalytics.map((x, idx) => {
                  let yValue = null;
                  if (sideType === sideValues[idx]) {
                    const num = parseFloat(prop.values[idx].toString());
                    yValue = isNaN(num) ? null : num;
                  }
                  return {
                    x: new Date(2021, 0, 0, 0, 0, 0, x),
                    y: yValue,
                  };
                });
                tempSeries.push({
                  name: `${seriesName(chartData.length, test.date, prop.name)}${
                    sideType ? `(${sideType})` : ''
                  }`,
                  shortName: prop.mlPropertyNameId,
                  data: data,
                  type: chartType === 'line' ? 'scatter' : chartType,
                  color: getRandomColor(
                    chartData.length > 1
                      ? indexProp
                      : selectedColumnsName.indexOf(prop.name),
                  ),
                  axisName: prop.unit || prop.name,
                });
                tempValuesAxis = generateValueAxis(tempValuesAxis, prop, data);
              });
            }
          });

        if (chartCategory === 'summary') {
          test.summary?.properties.forEach((prop) => {
            if (selectedColumnsName.includes(prop.name)) {
              const data = xAxisSummary.map((x, idx) => {
                return {
                  x,
                  y:
                    idx === indexProp
                      ? parseFloat(prop.values[0].toString()) || 0
                      : null,
                };
              });
              tempSeries.push({
                name: seriesName(chartData.length, test.date, prop.name),
                shortName: prop.mlPropertyNameId,
                data: data,
                type: chartType === 'line' ? 'scatter' : chartType,
                color: getRandomColor(
                  chartData.length > 1
                    ? indexProp
                    : selectedColumnsName.indexOf(prop.name),
                ),
                axisName: prop.unit || prop.name,
              });
              tempValuesAxis = generateValueAxis(tempValuesAxis, prop, data);
            }
          });
        }
      });
      setValueAxis(
        tempValuesAxis.map((v) => ({
          ...v,
          min: Math.floor(v.min > 0 ? v.min * 0.8 : v.min * 1.2),
          max: Math.ceil(v.max < 0 ? v.max * 0.8 : v.max * 1.2),
        })),
      );
      setSeries(tempSeries);
    }
    // eslint-disable-next-line
  }, [selectedColumns, chartData.length, chartType]);

  const legendVisualisation = (e: LegendItemVisualArgs) => {
    const defaultVisual: any = e.createVisual();
    if (e.series.markers.background) {
      defaultVisual.children[0].options.fill.set(
        'color',
        e.series.markers.background,
      );
      defaultVisual.children[0].options.stroke.set(
        'stroke',
        e.series.markers.background,
      );
    }
    return defaultVisual;
  };

  const itemRender = (li: any, itemProps: any) => {
    const item: ResultProperty = itemProps.dataItem;
    const itemChildren = (
      <span>
        {item.name}
        <span style={{ color: '#ABB2BF' }}> ({item.description})</span>
      </span>
    );

    return React.cloneElement(li, li.props, itemChildren);
  };

  const selectedValues = (
    list: ResultProperty[],
    selected: ResultProperty[],
  ) => {
    return selected.filter((s) => list.map((l) => l.name).includes(s.name));
  };

  const setValue = (newValues: ResultProperty[], list: ResultProperty[]) => {
    const columns = selectedColumns.filter(
      (s) => !list.map((l) => l.name).includes(s.name),
    );
    setSelectedColumns(columns.concat(newValues));
  };

  const formatXAxis = (e: any) => {
    const startDate = new Date(2021, 0, 0, 0, 0, 0, 0);
    if (startDate > e.value) {
      return ((e.value - startDate.valueOf()) / 1000).toFixed(1);
    }
    return moment(e.value).format(
      chartCategory === 'summary' ? 'HH:mm:ss DD MMM YYYY' : 's.S',
    );
  };

  return (
    <Wrap
      width={'100%'}
      margin={'0 0 3rem 0'}
      padding={'0 0 3rem 0'}
      borders={[{ side: 'bottom', color: accentColorSecond }]}
    >
      <Wrap
        flexDir={['column', 'row']}
        justifyCont={'space-between'}
        width={'100%'}
        margin={'0 0 2rem 0'}
      >
        <Wrap width={['100%', 'auto']} flexDir={['column', 'row']} grow={'1'}>
          <Wrap
            width={'100%'}
            maxWidth={'34rem'}
            margin={['2rem 0', '0 1.6rem 0 0']}
          >
            <StyledLabel>{propertyName}</StyledLabel>
            <StyledMultiSelect
              data={listProperties}
              value={selectedValues(listProperties, selectedColumns)}
              dataItemKey="name"
              textField="name"
              onChange={(event: any) => setValue(event.value, listProperties)}
              itemRender={itemRender}
            />
          </Wrap>
          {listSecondProperties && (
            <Wrap
              grow={'1'}
              margin={['2rem 0', '0']}
              width={'100%'}
              maxWidth={'34rem'}
            >
              <StyledLabel>{secondPropertyName}</StyledLabel>
              <StyledMultiSelect
                data={listSecondProperties}
                value={selectedValues(listSecondProperties, selectedColumns)}
                dataItemKey="name"
                textField="name"
                onChange={(event: any) =>
                  setValue(event.value, listSecondProperties)
                }
                itemRender={itemRender}
              />
            </Wrap>
          )}
        </Wrap>
        <Wrap
          width={'100%'}
          maxWidth={'9rem'}
          alignItems={'center'}
          justifyCont={'flex-end'}
        >
          <Button
            onClick={() => setChartType('line')}
            icon
            transparent
            children={
              <LineGraphIcon
                color={chartType === 'line' ? '#000' : fontColorSecond}
                width={'2rem'}
              />
            }
          />
          <Button
            onClick={() => setChartType('column')}
            icon
            transparent
            children={
              <RowGraphIcon
                color={chartType === 'column' ? '#000' : fontColorSecond}
                width={'2rem'}
              />
            }
          />
        </Wrap>
      </Wrap>
      <div className="k-card" style={{ height: 350, width: '100%' }}>
        {!!series?.length && (
          <Chart
            style={{ height: '100%', width: '100%' }}
            pannable={{ lock: 'y' }}
            zoomable={{ mousewheel: { lock: 'y' }, selection: { lock: 'y' } }}
            renderAs="canvas"
            transitions={false}
          >
            <ChartLegend position="bottom" orientation="horizontal">
              <ChartLegendItem visual={legendVisualisation} />
            </ChartLegend>
            <ChartCategoryAxis>
              <ChartCategoryAxisItem
                labels={{
                  visible: true,
                  content: formatXAxis,
                }}
                baseUnit={chartCategory === 'summary' ? 'auto' : 'milliseconds'}
                baseUnitStep={chartCategory === 'summary' ? 'auto' : step}
                maxDivisions={
                  chartCategory === 'summary' ? chartData.length : 50
                }
              >
                <ChartCategoryAxisCrosshair>
                  <ChartCategoryAxisCrosshairTooltip />
                </ChartCategoryAxisCrosshair>
              </ChartCategoryAxisItem>
            </ChartCategoryAxis>
            <ChartValueAxis>
              {valueAxis.map((item, idx) => (
                <ChartValueAxisItem
                  key={idx}
                  name={item.name}
                  min={item.min}
                  max={item.max}
                  labels={{ visible: true }}
                  title={{ text: item.title }}
                />
              ))}
            </ChartValueAxis>
            <ChartSeries>
              {series.map((s, idx) => (
                <ChartSeriesItem
                  key={idx}
                  data={s.data}
                  field="y"
                  tooltip={{
                    visible: true,
                    background: s.color,
                    format: `${s.name} - {0}`,
                  }}
                  categoryField="x"
                  type={s.type === 'scatter' ? 'line' : s.type}
                  // eslint-disable-next-line
                  style={'smooth'}
                  color={s.type === 'scatter' ? 'transparent' : s.color}
                  name={s.name}
                  markers={{
                    visible: s.type === 'scatter',
                    background: s.color,
                  }}
                  axis={s.axisName}
                />
              ))}
            </ChartSeries>
          </Chart>
        )}
        {!series?.length && (
          <Wrap width={'100%'} style={{ height: '100%' }}>
            <Row
              justifyContent={'center'}
              alignItems={'center'}
              style={{ height: '100%' }}
            >
              Please select data to display
            </Row>
          </Wrap>
        )}
      </div>
    </Wrap>
  );
};

export default memo(ChartResult);
